import { useMutation, useQuery } from '@tanstack/react-query'
import {
  App,
  Button,
  Card,
  Empty,
  Flex,
  Form,
  List,
  Select,
  Skeleton,
  Switch,
  Typography
} from 'antd'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  applicationsAccessCreateMutation,
  applicationsApplicationsAccessListOptions,
  applicationsApplicationsAccessListQueryKey,
  applicationsApplicationsListOptions,
  organizationsAccessRetrieveQueryKey,
  organizationsAccessRolesCreateMutation,
  type ApplicationDetail
} from 'src/api/main'
import { AppIcon } from 'src/features/apps/components/AppIcon'
import { useInvalidatePredicateQueries } from 'src/hooks'
import { useShowApiErrors } from 'src/utils'

import { orgEntityListCardHeaderStyles } from './workspaceOrgChart/OrgEntityListCard'

type ApplicationWithSettings = ApplicationDetail & {
  settings?: Record<string, unknown> | null
}

const PER_PAGE_MAX = 50

const { Text } = Typography

type Props = {
  /** User.id — used to filter the `applicationsApplicationsAccessList` query. */
  userId: number
  /**
   * OrganizationAccess.id — used both as the path id in
   * `organizationsAccessRolesCreate` and (counter-intuitively) inside the
   * `users` body field of `applicationsAccessCreate`. The backend expects
   * OrganizationAccess pks there, not User pks.
   */
  organizationAccessId: number
  /** Organization.id of the workspace. */
  workspaceId: number
}

type FormShape = Record<string, boolean | number | undefined>

const activeKey = (appId: number) => `${appId}_active`
const roleKey = (appId: number) => `${appId}_role`

/**
 * Mirrors WEB's `apps/organization/.../profile/components/ProfileTabs/Permissions.tsx`:
 * one row per app in workspace with a Switch (enable access) + single-role Select.
 * Uses the same endpoints WEB uses for the bulk replace-all flow.
 */
export const MemberAppPermissionsList = ({
  userId,
  organizationAccessId,
  workspaceId
}: Props) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const [form] = Form.useForm<FormShape>()

  const { data: appsPage, isLoading: isLoadingApps } = useQuery({
    ...applicationsApplicationsListOptions({
      query: { organization: workspaceId, per_page: PER_PAGE_MAX }
    }),
    enabled: workspaceId > 0
  })

  const { data: accessPage } = useQuery({
    ...applicationsApplicationsAccessListOptions({
      query: {
        user: userId,
        organization: workspaceId,
        per_page: PER_PAGE_MAX
      }
    }),
    enabled: userId > 0 && workspaceId > 0
  })

  const apps = useMemo<ApplicationWithSettings[]>(
    () => (appsPage?.results ?? []) as unknown as ApplicationWithSettings[],
    [appsPage?.results]
  )

  // Hydrate form from current access state.
  useEffect(() => {
    const next: FormShape = {}
    accessPage?.results?.forEach((access) => {
      next[activeKey(access.application.id)] = true
      const firstRoleId = access.roles[0]?.id
      if (firstRoleId !== undefined) {
        next[roleKey(access.application.id)] = firstRoleId
      }
    })
    form.setFieldsValue(next)
  }, [accessPage, form])

  const { mutateAsync: setRoles, isPending: isPendingRoles } = useMutation({
    ...organizationsAccessRolesCreateMutation()
  })

  const { mutate: createAccess, isPending: isPendingAccess } = useMutation({
    ...applicationsAccessCreateMutation(),
    onSuccess: async () => {
      const values = form.getFieldsValue()
      const roleIds = apps
        .filter((app) => values[activeKey(app.id)])
        .map((app) => values[roleKey(app.id)])
        .filter((v): v is number => typeof v === 'number')

      try {
        await setRoles({
          path: { id: organizationAccessId },
          body: { roles: roleIds }
        })
        await invalidateQueries([
          applicationsApplicationsAccessListQueryKey({
            query: { user: userId, organization: workspaceId }
          }),
          organizationsAccessRetrieveQueryKey({
            path: { id: organizationAccessId }
          })
        ])
        message.success(
          t('workspace.orgChart.memberDetail.appAccess.permissionsUpdated')
        )
      } catch (error) {
        showApiErrors(error)
      }
    },
    onError: (error) => showApiErrors(error)
  })

  const handleSubmit = async () => {
    if (!workspaceId) return
    try {
      await form.validateFields()
      const values = form.getFieldsValue()

      const applications = apps
        .filter((app) => values[activeKey(app.id)])
        .map((app) => {
          const roleId = values[roleKey(app.id)]
          const roleName =
            typeof roleId === 'number'
              ? (app.related_roles?.find((r) => r.id === roleId)?.name ?? null)
              : null

          return { id: app.id, role: roleName }
        })

      // The `users` field on this endpoint actually expects OrganizationAccess
      // ids (despite the name) — passing User.id triggers
      // "OrganizationAccess with pk:<n> does not exists" on the backend.
      createAccess({
        path: { organization_id: workspaceId },
        body: { users: [organizationAccessId], applications }
      })
    } catch {
      // form validation failed — handled by AntD
    }
  }

  const isPending = isPendingAccess || isPendingRoles

  return (
    <Card
      size="small"
      variant="outlined"
      title={t('workspace.orgChart.memberDetail.appAccess.sectionTitle')}
      styles={orgEntityListCardHeaderStyles}
    >
      <Form form={form} component={false}>
        <List
          itemLayout="horizontal"
          loading={isLoadingApps}
          dataSource={apps}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t(
                  'workspace.orgChart.memberDetail.appAccess.emptyState'
                )}
              />
            )
          }}
          renderItem={(item) => {
            const roles = item.related_roles ?? []
            const hasRole = roles.length > 0

            return (
              <List.Item key={item.id} style={{ paddingInline: 0 }}>
                <Flex align="center" justify="space-between" gap={16} flex={1}>
                  <Flex align="center" gap={12} flex={1}>
                    <AppIcon
                      type={item.instance_type.type}
                      photoUrl={item.icon?.file}
                      settings={item.settings}
                      size={40}
                    />
                    <Flex vertical>
                      <Text strong>{item.name}</Text>
                      {item.description ? (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {item.description}
                        </Text>
                      ) : null}
                    </Flex>
                  </Flex>

                  <Flex align="center" gap={24}>
                    <Form.Item
                      name={activeKey(item.id)}
                      valuePropName="checked"
                      noStyle
                    >
                      <Switch
                        onChange={(checked) => {
                          if (!checked) {
                            form.setFieldValue(roleKey(item.id), undefined)
                          }
                        }}
                      />
                    </Form.Item>

                    <Form.Item
                      shouldUpdate={(prev, curr) =>
                        prev[activeKey(item.id)] !== curr[activeKey(item.id)]
                      }
                      noStyle
                    >
                      {({ getFieldValue }) => {
                        const isActive = Boolean(
                          getFieldValue(activeKey(item.id))
                        )

                        return (
                          <Form.Item name={roleKey(item.id)} noStyle>
                            <Select
                              style={{ width: 220 }}
                              placeholder={
                                hasRole
                                  ? t(
                                      'workspace.orgChart.memberDetail.appAccess.selectRole'
                                    )
                                  : t(
                                      'workspace.orgChart.memberDetail.appAccess.noRoleAvailable'
                                    )
                              }
                              disabled={!hasRole || !isActive}
                              allowClear
                              options={roles.map((r) => ({
                                label: r.name,
                                value: r.id
                              }))}
                            />
                          </Form.Item>
                        )
                      }}
                    </Form.Item>
                  </Flex>
                </Flex>
              </List.Item>
            )
          }}
        >
          {isLoadingApps && apps.length === 0 ? (
            <List.Item>
              <Skeleton active title={false} paragraph={{ rows: 1 }} />
            </List.Item>
          ) : null}
        </List>

        <Flex justify="end" gap={8} style={{ marginTop: 16 }}>
          <Button onClick={() => form.resetFields()} disabled={isPending}>
            {t('cancel')}
          </Button>
          <Button
            type="primary"
            loading={isPending}
            onClick={() => void handleSubmit()}
          >
            {t('save')}
          </Button>
        </Flex>
      </Form>
    </Card>
  )
}
